import { BASE_URL } from '../constants/url-constants';

export type JobSearchGuideArticleId = 'finlandPillar' | 'applicationTracker';

export type JobSearchGuideLocale = 'en' | 'fi' | 'sv';

export type JobSearchGuidePublicSeo = {
  path: string;
  /** <title> — two `|` separators (keywords | value | brand). */
  title: string;
  description: string;
  /** Plain headline for breadcrumb / in-page context (no brand suffix). */
  headline: string;
  homeLabel: string;
  hubTitle: string;
  /** Pillar hub URL for “Job search” breadcrumb on spoke pages. */
  hubPath: string;
  articleSection: string;
  ogLocale: string;
  keywords: string;
  tags: string[];
  /** ISO date (YYYY-MM-DD) for Article schema. */
  datePublished: string;
};

const OG_IMAGE = `${BASE_URL}/logo_jobcrawls_horizontal.png`;

export const JOB_SEARCH_GUIDE_OG_IMAGE = OG_IMAGE;

const PILLAR: Record<JobSearchGuideLocale, JobSearchGuidePublicSeo> = {
  en: {
    path: '/en/job-search/how-to-find-a-job-in-finland',
    title: 'How to find a job in Finland | Live listings & application tracking | JobCrawls',
    description:
      'Practical 2026 guide: where Finnish jobs are posted, how to cover career pages and Työmarkkinatori without juggling every site, and how JobCrawls keeps search and tracking in one workflow.',
    headline: 'How to find a job in Finland',
    homeLabel: 'Home',
    hubTitle: 'Job search',
    hubPath: '/en/job-search/how-to-find-a-job-in-finland',
    articleSection: 'Jobs in Finland',
    ogLocale: 'en_US',
    keywords:
      'find a job Finland, jobs Finland, Työmarkkinatori, Finnish employers, job search Finland, career pages Finland, remote jobs Finland, JobCrawls',
    tags: ['Job search', 'Finland', 'Työmarkkinatori', 'Career pages', 'Remote work'],
    datePublished: '2026-05-12',
  },
  fi: {
    path: '/fi/tyonhaku/miten-loydat-toita-suomessa',
    title: 'Työnhaku Suomessa | Lähteet, seuranta ja ilmoitukset yhdessä | JobCrawls',
    description:
      'Käytännön opas 2026: mistä suomalaiset työpaikat löytyvät, miten kattaa yritysten urasivut ja Työmarkkinatori ilman jokaista sivustoa erikseen, ja miten JobCrawls yhdistää haun ja hakemusten seurannan.',
    headline: 'Miten löydät töitä Suomessa',
    homeLabel: 'Etusivu',
    hubTitle: 'Työnhaku',
    hubPath: '/fi/tyonhaku/miten-loydat-toita-suomessa',
    articleSection: 'Työelämä',
    ogLocale: 'fi_FI',
    keywords:
      'työnhaku Suomi, avoimet työpaikat, Työmarkkinatori, urasivut, etätyö Suomi, työpaikat yhdellä haulla, JobCrawls',
    tags: ['Työnhaku', 'Suomi', 'Työmarkkinatori', 'Urasivut', 'Etätyö'],
    datePublished: '2026-05-12',
  },
  sv: {
    path: '/sv/jobbsok/hur-hittar-man-jobb-i-finland',
    title: 'Hitta jobb i Finland | Källor, uppföljning och annonser samlade | JobCrawls',
    description:
      'Praktisk guide 2026: var finska jobb finns, hur du täcker karriärsidor och Työmarkkinatori utan att besöka varje sajt, och hur JobCrawls samlar sökning och ansökningsspårning i ett arbetsflöde.',
    headline: 'Hur hittar man jobb i Finland',
    homeLabel: 'Hem',
    hubTitle: 'Jobbsökning',
    hubPath: '/sv/jobbsok/hur-hittar-man-jobb-i-finland',
    articleSection: 'Karriärresurser',
    ogLocale: 'sv_SE',
    keywords:
      'jobb Finland, hitta jobb Finland, Työmarkkinatori, karriärsidor, distansjobb Finland, jobbsökning, JobCrawls',
    tags: ['Jobbsökning', 'Finland', 'Työmarkkinatori', 'Karriärsidor', 'Distansjobb'],
    datePublished: '2026-05-12',
  },
};

const TRACKER: Record<JobSearchGuideLocale, JobSearchGuidePublicSeo> = {
  en: {
    path: '/en/job-search/job-application-tracker',
    title: 'Job application tracker Finland | Fields, follow-ups & one search | JobCrawls',
    description:
      'Track Finnish applications without a spreadsheet: what to log per role, realistic follow-up timing, duplicate applications across sources, and how JobCrawls ties status to live listings.',
    headline: 'Job application tracker',
    homeLabel: 'Home',
    hubTitle: 'Job search',
    hubPath: '/en/job-search/how-to-find-a-job-in-finland',
    articleSection: 'Jobs in Finland',
    ogLocale: 'en_US',
    keywords:
      'job application tracker, track applications Finland, job search spreadsheet, follow up job application Finland, Työmarkkinatori, JobCrawls',
    tags: ['Job search', 'Applications', 'Finland', 'Tracking', 'Follow-up'],
    datePublished: '2026-05-12',
  },
  fi: {
    path: '/fi/tyonhaku/tyonhakemuksen-seuranta',
    title: 'Työhakemusten seuranta | Kentät, seuranta ja yksi työkalu | JobCrawls',
    description:
      'Seuraa hakemuksia ilman taulukkoa: mitä kirjata jokaista paikkaa varten, milloin otat yhteyttä Suomessa, miten vältät tuplahakemukset eri lähteistä ja miten JobCrawls sitoo tilan eläviin ilmoituksiin.',
    headline: 'Työhakemusten seuranta',
    homeLabel: 'Etusivu',
    hubTitle: 'Työnhaku',
    hubPath: '/fi/tyonhaku/miten-loydat-toita-suomessa',
    articleSection: 'Työelämä',
    ogLocale: 'fi_FI',
    keywords:
      'työhakemusten seuranta, työnhaku taulukko, seuraa hakemuksia, yhteydenotto hakemuksen jälkeen, Työmarkkinatori, JobCrawls',
    tags: ['Työnhaku', 'Hakemukset', 'Seuranta', 'Suomi', 'Työmarkkinatori'],
    datePublished: '2026-05-12',
  },
  sv: {
    path: '/sv/jobbsok/jobbansokningssparare',
    title: 'Jobbansökningsspårare Finland | Fält, uppföljning & en sökning | JobCrawls',
    description:
      'Spåra ansökningar utan kalkylark: vad du loggar per roll, rimliga uppföljningsfönster i Finland, dubbletter mellan källor och hur JobCrawls kopplar status till aktiva annonser.',
    headline: 'Jobbansökningsspårare',
    homeLabel: 'Hem',
    hubTitle: 'Jobbsökning',
    hubPath: '/sv/jobbsok/hur-hittar-man-jobb-i-finland',
    articleSection: 'Karriärresurser',
    ogLocale: 'sv_SE',
    keywords:
      'jobbansökning spårning, följa upp ansökan Finland, jobbsökning kalkylark, Työmarkkinatori, JobCrawls',
    tags: ['Jobbsökning', 'Ansökningar', 'Uppföljning', 'Finland'],
    datePublished: '2026-05-12',
  },
};

export function getJobSearchGuidePublicSeo(
  article: JobSearchGuideArticleId,
  locale: JobSearchGuideLocale,
): JobSearchGuidePublicSeo {
  return article === 'finlandPillar' ? PILLAR[locale] : TRACKER[locale];
}

export function getTrilingualUrlsForJobSearchArticle(article: JobSearchGuideArticleId): {
  en: string;
  fi: string;
  sv: string;
} {
  const en = `${BASE_URL}${getJobSearchGuidePublicSeo(article, 'en').path}`;
  const fi = `${BASE_URL}${getJobSearchGuidePublicSeo(article, 'fi').path}`;
  const sv = `${BASE_URL}${getJobSearchGuidePublicSeo(article, 'sv').path}`;
  return { en, fi, sv };
}
